import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLAN_LABEL = {
  monthly: "Monthly",
  biweekly: "Bi-Weekly",
  onetime: "One-Time",
};

export default function SignupSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState({ status: "verifying", message: "" });
  const [pricing, setPricing] = useState(null);
  const [planCode, setPlanCode] = useState(null);
  const finalizedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({
        status: "error",
        message: "Missing checkout session. Please try signing up again.",
      });
      return;
    }
    if (finalizedRef.current) return;
    finalizedRef.current = true;

    const finalize = async () => {
      try {
        const { data } = await api.post("/auth/signup/finalize", { session_id: sessionId });
        setSession(data.token, data.customer);
        setPricing(data.pricing);
        setPlanCode(data.customer?.plan_code);
        setState({ status: "ok", message: "" });
        toast.success("Welcome to Silky Smooth!", {
          description: data.pricing?.coupon_code
            ? `${data.pricing.coupon_code} saved you $${(data.pricing.discount_amount_cents / 100).toFixed(2)}.`
            : "Your subscription is active.",
        });
        // Auto-redirect after 2.5s so the user gets a confirmation moment.
        const t = setTimeout(() => navigate("/portal", { replace: true }), 2500);
        return () => clearTimeout(t);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 402) {
          // Payment incomplete — kick them back to step 5
          toast.error("Payment didn't complete", {
            description: "Please try again or use a different card.",
          });
          navigate("/signup?step=5&error=payment_failed", { replace: true });
          return;
        }
        setState({
          status: "error",
          message: formatApiError(err) || "We couldn't verify your payment.",
        });
      }
    };

    finalize();
  }, [sessionId, navigate, setSession]);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center px-4 py-16"
      data-testid="page-signup-success"
    >
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-soft p-8 sm:p-10 text-center">
        {state.status === "verifying" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-accent-100 flex items-center justify-center mb-5">
              <Loader2 className="h-7 w-7 text-accent-700 animate-spin" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="success-title-verifying">
              Confirming your payment…
            </h1>
            <p className="text-sm text-slate-600">
              Hang tight — we're checking with Stripe and activating your subscription.
            </p>
          </>
        )}

        {state.status === "ok" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="success-title-ok">
              You're in. Bins incoming.
            </h1>
            <p className="text-sm text-slate-600 mb-6">
              {planCode
                ? `Your ${PLAN_LABEL[planCode] || planCode} plan is active.`
                : "Your subscription is active."}{" "}
              Check your email for a fresh receipt and your first scheduled cleaning.
            </p>
            {pricing && (
              <div
                className="rounded-xl bg-brand-50 border border-brand-100 p-4 text-left mb-6"
                data-testid="success-pricing"
              >
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>Charged</span>
                  {pricing.coupon_code && pricing.discount_amount_cents > 0 ? (
                    <span className="line-through">${(pricing.original_amount_cents / 100).toFixed(2)}</span>
                  ) : null}
                </div>
                <div className="font-heading text-3xl font-extrabold text-ink">
                  ${(pricing.final_amount_cents / 100).toFixed(2)}
                </div>
                {pricing.coupon_code && pricing.discount_amount_cents > 0 && (
                  <p className="mt-2 text-xs font-bold text-accent-700">
                    {pricing.coupon_code} saved you ${(pricing.discount_amount_cents / 100).toFixed(2)}
                  </p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate("/portal", { replace: true })}
              className="btn-primary w-full"
              data-testid="success-go-portal"
            >
              Go to my portal
            </button>
            <p className="mt-4 text-[11px] text-slate-400">Redirecting automatically…</p>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mb-5">
              <TriangleAlert className="h-7 w-7 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-2" data-testid="success-title-error">
              We couldn't confirm your payment
            </h1>
            <p className="text-sm text-slate-600 mb-6" data-testid="success-error-message">
              {state.message}
            </p>
            <div className="flex flex-col gap-2.5">
              <Link
                to="/signup?step=5"
                className="btn-primary w-full"
                data-testid="success-retry"
              >
                Retry signup
              </Link>
              <Link
                to="/contact"
                className="btn-secondary w-full"
                data-testid="success-contact"
              >
                Contact support
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
